import { Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { ModalFormContainerComponent } from '../../../../../../components/ui/modal-form-container/modal-form-container.component';
import { SearchInputComponent } from '../../../../../../components/inputs/search-input/search-input.component';
import { PaginationComponent } from '../../../../../../components/inputs/pagination/pagination.component';
import { IContact } from '../../../../../../types/contactModule';
import _ from 'lodash';
import { DataTableComponent } from '../../../../../../components/ui/data-table/data-table.component';
import { formatCount } from '../../../../../../../utils/sms';

@Component({
  selector: 'message-selected-contacts',
  standalone: true,
  imports: [
    ModalFormContainerComponent,
    SearchInputComponent,
    PaginationComponent,
    DataTableComponent,
  ],
  templateUrl: './message-selected-contacts.component.html',
  styleUrls: ['./message-selected-contacts.component.css', '../selected-list.css'],
})
export class MessageSelectedContactsComponent {
  constructor() {}
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @Input() open: boolean = false;
  @Output() close = new EventEmitter();
  @Input() selectedContacts: IContact[] = [];
  @Output() removeSelectedContacts = new EventEmitter<IContact[]>();
  @Output() removeAllSelectedContacts = new EventEmitter<void>();

  checkedContacts: IContact[] = [];
  formatCount = formatCount;

  /** Client-side filter over the chosen contacts. */
  term: string = '';
  /** "Remove all" asks once before it empties the list. */
  confirmRemoveAll: boolean = false;

  onSearch(term: string) {
    this.term = term ?? '';
    this.currentPage = 1;
  }

  get filteredContacts(): IContact[] {
    const term = this.term.trim().toLowerCase();
    if (!term) return this.selectedContacts;
    const compactTerm = term.replace(/\s+/g, '');
    return this.selectedContacts.filter((contact) => {
      const name = `${contact.fullName || ''} ${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
      const phone = String(contact.primaryContactNumber || '').replace(/\s+/g, '');
      const email = String(contact.emailAddress || '').toLowerCase();
      return name.includes(term) || phone.includes(compactTerm) || email.includes(term);
    });
  }

  contactName(contact: IContact): string {
    const name =
      (contact.fullName || '').trim() ||
      [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
    return name || 'Unnamed contact';
  }

  // pagination related properties and functions
  currentPage: number = 1;
  pageSize: number = 10;
  // paginate function
  paginate(array: IContact[], page = 1, pageSize = 10) {
    const offset = (page - 1) * pageSize;
    return _.slice(array, offset, offset + pageSize);
  }

  paginateWithMeta(array: IContact[], page = 1, pageSize = 10) {
    const total = array.length;
    const totalPages = Math.ceil(total / pageSize);
    const data = this.paginate(array, page, pageSize);

    return {
      data,
      pagination: {
        page,
        pageSize,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
  }

  get paginatedContacts() {
    return this.paginateWithMeta(
      this.filteredContacts,
      this.currentPage,
      this.pageSize
    );
  }

  goToNextPage() {
    if (this.paginatedContacts.pagination.hasNext) {
      this.currentPage += 1;
    }
  }

  goToPreviousPage() {
    if (this.paginatedContacts.pagination.hasPrev) {
      this.currentPage -= 1;
    }
  }

  /** app-pagination counts pages from 0; this list counts from 1. */
  updatePage(page: number) {
    this.currentPage = page + 1;
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
  }

  toggleSelectContact(contact: IContact) {
    const checkedContactsIds = this.checkedContacts.map((c) => c.id);
    const currentContactPageIds = this.paginatedContacts.data.map((c) => c.id);

    if (checkedContactsIds.includes(contact.id)) {
      // Deselect contact
      this.checkedContacts = this.checkedContacts.filter(
        (c) => c.id !== contact.id
      );
    } else {
      // Select contact
      this.checkedContacts = [...this.checkedContacts, contact];
    }
  }

  toggleSelectAllContacts() {
    const checkedContactsIds = this.checkedContacts.map((c) => c.id);
    const currentContactPageIds = this.paginatedContacts.data.map((c) => c.id);
    const allCurrentPageSelected = currentContactPageIds.every((id) =>
      checkedContactsIds.includes(id)
    );

    if (allCurrentPageSelected) {
      // Deselect all contacts on the current page
      this.checkedContacts = this.checkedContacts.filter(
        (c) => !currentContactPageIds.includes(c.id)
      );
    } else {
      // Select all contacts on the current page
      const contactsToAdd = this.paginatedContacts.data.filter(
        (c) => !checkedContactsIds.includes(c.id)
      );
      this.checkedContacts = [...this.checkedContacts, ...contactsToAdd];
    }
  }

  get isContactChecked() {
    return (contact: IContact) => {
      return this.checkedContacts.some((c) => c.id === contact.id);
    };
  }

  get allContactsSelected(): boolean {
    const checkedContactsIds = this.checkedContacts.map((c) => c.id);
    const currentContactPageIds = this.paginatedContacts.data.map((c) => c.id);
    return (
      currentContactPageIds.length > 0 &&
      currentContactPageIds.every((id) => checkedContactsIds.includes(id))
    );
  }

  removeSelection() {
    this.removeSelectedContacts.emit(this.checkedContacts);
    this.checkedContacts = [];
  }

  /** Ask before emptying the list; focus the safe choice, "Keep them". */
  askRemoveAll() {
    this.confirmRemoveAll = true;
    this.focusBulk('keep');
  }

  /** Back out of "Remove all"; focus returns to the button that asked. */
  cancelRemoveAll() {
    this.confirmRemoveAll = false;
    this.focusBulk('remove-all');
  }

  /** The bulk buttons swap in place, so move focus to the one that replaced the pressed one. */
  private focusBulk(which: 'keep' | 'remove-all') {
    setTimeout(() =>
      this.host.nativeElement.querySelector<HTMLElement>(`[data-bulk="${which}"]`)?.focus()
    );
  }

  removeAllSelection() {
    this.removeAllSelectedContacts.emit();
    this.checkedContacts = [];
    this.confirmRemoveAll = false;
    this.currentPage = 1;
  }

  closeModal() {
    this.term = '';
    this.confirmRemoveAll = false;
    this.close.emit();
  }
}
